// src/components/AdminPartners.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Form, Container, Alert, Spinner } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import { FaFolderOpen, FaAngleUp, FaAngleDown } from "react-icons/fa";
import "toastr/build/toastr.min.css";
import toastr from "toastr";
import InactivePartners from "./modals/InactivePartners";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import AddPartnerModal from "./modals/AddPartnerModal";
import PaginationComponent from "./PaginationComponent";
import UpcomingBirthdays from "./analitica/UpcomingBirthdays";

export default function AdminPartners() {
  // data
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);

  // search & filters
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");   // name | promo | email | id
  const [statusFilter, setStatusFilter] = useState("ALL");  // ALL | GOLD | SILVER | STARTER

  // sort
  const [sortField, setSortField] = useState("date_added");
  const [sortOrder, setSortOrder] = useState("desc");

  // modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);

  // new partner
  const [newPartner, setNewPartner] = useState({
    name: "",
    promo_code: "",
    email: "",
    phone: "",
    address: "",
  });

  // ui
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

  // toastr once
  useEffect(() => {
    toastr.options = {
      closeButton: true,
      progressBar: true,
      positionClass: "toast-top-right",
      showDuration: "300",
      hideDuration: "1000",
      timeOut: "4000",
      extendedTimeOut: "800",
      newestOnTop: true,
    };
  }, []);

  // fetch
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await fetch(`${apiUrl}/admin/partners`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setPartners(data);
          setFilteredPartners(data);
        } else if (res.status === 401) {
          navigate("/login");
        } else if (res.status === 403) {
          setError("У вас нет доступа к этому разделу");
        } else {
          setError("Ошибка при получении данных");
        }
      } catch {
        setError("Ошибка соединения с сервером");
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, [navigate, apiUrl]);

  // filter + sort
  useEffect(() => {
    const term = (searchTerm || "").toLowerCase();

    const byStatus = (p) =>
      statusFilter === "ALL"
        ? true
        : (p.partner_status || "").toUpperCase() === statusFilter;

    const byField = (p) => {
      if (!term) return true;
      switch (searchField) {
        case "promo":
          return String(p.promo_code || "").toLowerCase().includes(term);
        case "email":
          return String(p.partner_email || p.email || "").toLowerCase().includes(term);
        case "id":
          return String(p.unique_id || p.partner_id || "").toLowerCase().includes(term);
        case "name":
        default:
          return String(p.partner_name || "").toLowerCase().includes(term);
      }
    };

    const filtered = partners.filter((p) => byStatus(p) && byField(p));

    const sorted = filtered.sort((a, b) => {
      const A = a?.[sortField] ?? "";
      const B = b?.[sortField] ?? "";
      if (typeof A === "number" && typeof B === "number") {
        return sortOrder === "asc" ? A - B : B - A;
      }
      return sortOrder === "asc"
        ? String(A).localeCompare(String(B))
        : String(B).localeCompare(String(A));
    });

    setFilteredPartners(sorted);
    setCurrentPage(1);
  }, [searchTerm, searchField, statusFilter, sortField, sortOrder, partners]);

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const handleAddPartner = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/admin/partner/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newPartner),
      });
      const data = await response.json();
      if (response.ok) {
        const partnersResponse = await fetch(`${apiUrl}/admin/partners`, {
          credentials: "include",
        });
        if (partnersResponse.ok) {
          const partnersData = await partnersResponse.json();
          setPartners(partnersData);
          setFilteredPartners(partnersData);
        }

        setNewPartner({
          name: "",
          promo_code: "",
          email: "",
          phone: "",
          address: "",
        });
        setShowAddModal(false);
        if (data.email_sent) {
          toastr.success("Партнёр успешно добавлен, письмо отправлено");
        } else {
          toastr.warning("Партнёр создан, но письмо не было отправлено");
        }
      } else {
        toastr.error(data.error || "Ошибка при добавлении партнёра");
      }
    } catch {
      toastr.error("Ошибка соединения с сервером");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;
    const partnerIdToDelete = partnerToDelete.partner_id;
    try {
      const res = await fetch(`${apiUrl}/admin/partner/${partnerIdToDelete}/delete`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setPartners((prev) => prev.filter((p) => p.partner_id !== partnerIdToDelete));
        setShowDeleteModal(false);
        toastr.success("Партнёр успешно удалён");
      } else {
        toastr.error("Ошибка при удалении партнёра");
      }
    } catch {
      toastr.error("Ошибка соединения с сервером");
    }
  };

  // paging view
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentItems = useMemo(
  () => filteredPartners.slice(indexOfFirstItem, indexOfLastItem),
  [filteredPartners, indexOfFirstItem, indexOfLastItem]
);

const fmt = (n) => new Intl.NumberFormat("ru-RU").format(n || 0);
const fmtCurrency = (value) =>
  new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
const statusChip = (s) =>
  s === "GOLD" ? "chip chip-gold" : s === "SILVER" ? "chip chip-silver" : "chip chip-starter";
const metrics = useMemo(() => {
  const base = Array.isArray(partners) ? partners : [];
  const aggregate = base.reduce(
    (acc, partner) => {
      const status = String(partner.partner_status || "").toUpperCase();
      if (status === "GOLD") acc.gold += 1;
      else if (status === "SILVER") acc.silver += 1;
      else acc.starter += 1;
      acc.totalOrders += Number(partner.total_orders) || 0;
      acc.totalVolume += Number(partner.total_sum) || 0;
      acc.payoutSum += Number(partner.payout_percentage) || 0;
      return acc;
    },
    { gold: 0, silver: 0, starter: 0, totalOrders: 0, totalVolume: 0, payoutSum: 0 }
  );
  const averageCheck =
    aggregate.totalOrders > 0 ? aggregate.totalVolume / aggregate.totalOrders : 0;
  const avgPayout = base.length > 0 ? aggregate.payoutSum / base.length : 0;
  return {
    totalPartners: base.length,
    ...aggregate,
    averageCheck,
    avgPayout,
  };
}, [partners]);
const heroStats = [
  {
    label: "Партнёров",
    value: fmt(metrics.totalPartners),
    hint: `GOLD ${fmt(metrics.gold)} · SILVER ${fmt(metrics.silver)}`,
  },
  {
    label: "Всего заказов",
    value: fmt(metrics.totalOrders),
    hint: `Средний чек ${fmtCurrency(metrics.averageCheck)} EUR`,
  },
  {
    label: "Объём продаж",
    value: `${fmtCurrency(metrics.totalVolume)} EUR`,
    hint: "За весь период",
  },
  {
    label: "Средняя ставка",
    value: `${metrics.avgPayout.toFixed(1)}%`,
    hint: "По активным партнёрам",
  },
];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="secondary" />
        <p className="ms-3">Загрузка данных…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="d-flex flex-column align-items-center justify-content-center vh-100">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => navigate("/dashboard")}>Вернуться на Dashboard</Button>
      </Container>
    );
  }

  return (
    <Container fluid="xl" className="compact py-3">
      <section className="hero-board">
        <article className="hero-card hero-card--mosaic">
          <div className="hero-head">
            <div>
              <p className="eyebrow text-muted mb-1">Админ-панель</p>
              <h1 className="hero-heading mb-1">Партнёрская сеть</h1>
              <div className="hero-email">Управление партнёрами в едином окне</div>
              
            </div>
            <div className="hero-head__status hero-head__status--admin">
              <Button
                className="btn-outline-brand btn-rounded"
                variant="outline-danger"
                onClick={() => setShowAddModal(true)}
              >
                + Добавить партнёра
              </Button>
            </div>
          </div>
          <div className="hero-bar">
            {heroStats.map((stat) => (
              <div className="hero-bar__item" key={stat.label}>
                <span className="hero-bar__label">{stat.label}</span>
                <span className="hero-bar__value">{stat.value}</span>
              </div>
            ))}
          </div>
          <div className="hero-info-grid">
            <div className="hero-info-card">
              <span className="hero-info-card__label">GOLD</span>
              <span className="hero-info-card__value">{fmt(metrics.gold)}</span>
            </div>
            <div className="hero-info-card">
              <span className="hero-info-card__label">SILVER</span>
              <span className="hero-info-card__value">{fmt(metrics.silver)}</span>
            </div>
            <div className="hero-info-card">
              <span className="hero-info-card__label">STARTER</span>
              <span className="hero-info-card__value">{fmt(metrics.starter)}</span>
            </div>
          </div>
        </article>
      </section>

      <section className="panel panel--filters">
        <div className="panel-header">
          <div>
            <p className="eyebrow text-muted mb-1">Фильтры</p>
            <h2 className="h5 mb-0">Настройте выдачу</h2>
          </div>
          <div className="panel-actions">
            <span className="chip chip-ghost">Всего {fmt(partners.length)}</span>
            <Button
              variant="link"
              className="btn-ghost"
              onClick={() => {
                setSearchTerm("");
                setSearchField("name");
                setStatusFilter("ALL");
                setItemsPerPage(10);
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>
        <InactivePartners />
        <div className="filter-grid">
          <div className="filter-field">
            <label>Искать по</label>
            <Form.Select value={searchField} onChange={(e) => setSearchField(e.target.value)}>
              <option value="name">Имя партнёра</option>
              <option value="promo">Промокод</option>
              <option value="email">Email</option>
              <option value="id">ID</option>
            </Form.Select>
          </div>
          <div className="filter-field filter-field--grow">
            <label>Поиск</label>
            <Form.Control
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                searchField === "promo"
                  ? "Например: SPRING2025"
                  : searchField === "email"
                  ? "user@mail.com"
                  : searchField === "id"
                  ? "Уникальный ID партнёра"
                  : "Имя партнёра"
              }
            />
          </div>
          <div className="filter-field">
            <label>Статус</label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">Любой</option>
              <option value="GOLD">GOLD</option>
              <option value="SILVER">SILVER</option>
              <option value="STARTER">STARTER</option>
            </Form.Select>
          </div>
          <div className="filter-field">
            <label>На странице</label>
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              title="Элементов на странице"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / стр.</option>
              ))}
            </Form.Select>
          </div>
        </div>
      </section>

      <section className="panel panel--table partners-panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow text-muted mb-1">Реестр</p>
            <h2 className="h5 mb-0">Список партнёров</h2>
          </div>
          <span className="chip chip-ghost">Отфильтровано {fmt(filteredPartners.length)}</span>
        </div>
        <div className="table-wrap">
          <table className="table table-enterprise mb-0">
            <thead>
              <tr>
                <th
                  className={`th-sortable ${sortField === "date_added" ? "th-active" : ""}`}
                  onClick={() => handleSort("date_added")}
                >
                  Дата создания {sortField === "date_added" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "partner_status" ? "th-active" : ""}`}
                  onClick={() => handleSort("partner_status")}
                >
                  Статус {sortField === "partner_status" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "partner_name" ? "th-active" : ""}`}
                  onClick={() => handleSort("partner_name")}
                >
                  ФИО {sortField === "partner_name" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "promo_code" ? "th-active" : ""}`}
                  onClick={() => handleSort("promo_code")}
                >
                  Промокод {sortField === "promo_code" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "total_orders" ? "th-active" : ""}`}
                  onClick={() => handleSort("total_orders")}
                >
                  Заказов, шт {sortField === "total_orders" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "total_sum" ? "th-active" : ""}`}
                  onClick={() => handleSort("total_sum")}
                >
                  Общая сумма {sortField === "total_sum" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th
                  className={`th-sortable ${sortField === "payout_percentage" ? "th-active" : ""}`}
                  onClick={() => handleSort("payout_percentage")}
                >
                  Выплаты, % {sortField === "payout_percentage" && (sortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                </th>
                <th style={{ width: 120 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((partner) => (
                <tr key={partner.partner_id}>
                  <td>{partner.date_added}</td>
                  <td><span className={statusChip(partner.partner_status)}>{partner.partner_status}</span></td>
                  <td>{partner.partner_name}</td>
                  <td>{partner.promo_code || "—"}</td>
                  <td>{fmt(partner.total_orders)}</td>
                  <td>{fmt(partner.total_sum)} EUR</td>
                  <td>{partner.payout_percentage}%</td>
                  <td>
                    <div className="table-actions">
                      <Link to={`/admin/partner/${partner.unique_id}`} className="btn btn-ghost" title="Открыть">
                        <FaFolderOpen />
                      </Link>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        title="Удалить"
                        onClick={() => { setPartnerToDelete(partner); setShowDeleteModal(true); }}
                      >
                        <MdDelete />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <PaginationComponent
        currentPage={currentPage}
        totalItems={filteredPartners.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />

      {/* Модалки */}
      <AddPartnerModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        handleAddPartner={handleAddPartner}
        newPartner={newPartner}
        setNewPartner={setNewPartner}
        loading={loading}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeletePartner}
        partnerName={partnerToDelete?.partner_name}  // ← фикс поля
      />

      {/* Аналитика снизу страницы */}
      <div className="mt-3">
        <UpcomingBirthdays />
      </div>
    </Container>
  );
}
